/* eslint-disable import/prefer-default-export */
import { OperationVariables } from '@apollo/client';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import { PostQueryOperations } from '../../../@types';
import { filterData } from './filter';

type Row = Record<string, unknown>;
type SubscriptionEvent = 'insert' | 'update' | 'delete' | 'soft-delete';

/**
 * Subscription handler
 *
 * NOTE: Many components might be subscribed for events on the same object.
 * This method will filter current data and also incoming data to avoid data getting stuck in the wrong component after update.
 */
export const getSubscriptionHandler =
  (
    rootKey: string,
    eventKey: string,
    queryRootKey: string,
    postQuery?: PostQueryOperations,
    refetch?: () => void,
  ) =>
  (prev: Record<string, unknown>, { subscriptionData }: OperationVariables) => {
    if (!subscriptionData.data) return prev;

    /** If refetch is enabled, refetch and skip subscription handling */
    if (refetch) {
      refetch();
      return prev;
    }

    /** The event string */
    const event = get(
      subscriptionData.data,
      eventKey,
      undefined,
    ) as SubscriptionEvent;

    /**
     * The currently cached data.
     * It's composed by the previous query result with any cache updates, including data updated by this subscription
     * This is why accessing prev (or data or cachedData in this case) might give you the old data with the updated resource
     * */
    const cachedData = get(prev, queryRootKey, []) as Row[];

    /** The incoming affected row */
    const newRow = get(subscriptionData.data, rootKey, {}) as Row;

    /** The data to be returned  */
    const result = cloneDeep(prev);
    let tempData = cloneDeep(cachedData);

    /** Upsert */
    if (event === 'insert' || event === 'update') {
      let updated = false;
      tempData = cachedData.map((row: Row) => {
        if (row.id === newRow.id) {
          updated = true;
          return newRow;
        }
        return row;
      });

      if (!updated) {
        tempData.push(newRow);
      }
    }

    /** Delete */
    if (event === 'delete' || event === 'soft-delete') {
      tempData = cachedData.filter((row: Row) => row.id !== newRow.id);
    }

    /**
     * Filter and sort tempData by postQuery parameters
     * This step should remove data mistakenly inserted on Upsert if it doesn't match postQuery.filters
     * */
    tempData = filterData(tempData, undefined, postQuery);

    /** Set result for returning */
    set(result, queryRootKey, tempData);

    /** Debug */
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log({
        event,
        cachedData,
        newRow,
        postQuery,
        subscriptionData: subscriptionData.data,
        tempData,
        result,
      });
    }

    return result;
  };
